import { useState, useEffect } from "react";
import axios from "axios";
import { useLang } from "../contexts/LangContext";
import { useAuth } from "../contexts/AuthContext";
import { Settings, Plus, Edit, Trash2, UserCheck, UserX } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_CLASS = {
    admin: "badge-debtor", staff: "badge-active",
    support: "badge-pending", client: "badge-stock"
};

const defaultForm = { name: "", email: "", nipt: "", password: "", role: "staff", user_type: "staff", is_active: true };

export default function UsersPage() {
    const { t } = useLang();
    const { user: currentUser } = useAuth();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editItem, setEditItem] = useState(null);
    const [form, setForm] = useState(defaultForm);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/users`, { withCredentials: true });
            setUsers(data);
        } catch (e) { console.error(e); }
        setLoading(false);
    };

    useEffect(() => { fetchUsers(); }, []);

    const openAdd = () => { setEditItem(null); setForm(defaultForm); setError(""); setShowModal(true); };
    const openEdit = (item) => {
        setEditItem(item);
        setForm({ name: item.name, email: item.email || "", nipt: item.nipt || "", password: "", role: item.role, user_type: item.user_type || "staff", is_active: item.is_active !== false });
        setError(""); setShowModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault(); setSaving(true); setError("");
        try {
            const payload = { ...form };
            if (!payload.password) delete payload.password;
            if (payload.user_type === "staff") delete payload.nipt;
            if (payload.user_type === "client") delete payload.email;
            if (editItem) {
                await axios.put(`${API}/users/${editItem.id}`, payload, { withCredentials: true });
            } else {
                await axios.post(`${API}/users`, payload, { withCredentials: true });
            }
            setShowModal(false); fetchUsers();
        } catch (e) { setError(e.response?.data?.detail || t("error")); }
        setSaving(false);
    };

    const handleDelete = async (item) => {
        if (item.id === currentUser?.id || item._id === currentUser?._id) { alert("Nuk mund të fshini llogarinë tuaj."); return; }
        if (!window.confirm(t("confirm_delete"))) return;
        try {
            await axios.delete(`${API}/users/${item.id}`, { withCredentials: true });
            fetchUsers();
        } catch (e) { alert(e.response?.data?.detail || t("error")); }
    };

    const handleToggleActive = async (item) => {
        try {
            await axios.put(`${API}/users/${item.id}`, { is_active: !item.is_active }, { withCredentials: true });
            fetchUsers();
        } catch (e) { alert(t("error")); }
    };

    const f = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

    const ROLE_LABELS = { admin: "Admin", staff: "Staf", support: "Support", client: "Klient" };

    return (
        <div className="p-6">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                        <Settings size={20} className="text-[#E7212E]" /> {t("users")}
                    </h1>
                    <p className="text-sm text-slate-500 mt-0.5">Menaxhimi i aksesit në sistem</p>
                </div>
                <button onClick={openAdd} data-testid="add-user-btn"
                    className="flex items-center gap-2 bg-[#E7212E] hover:bg-[#C91C26] text-white text-sm font-medium px-4 py-2 rounded transition-colors">
                    <Plus size={15} /> {t("add")}
                </button>
            </div>

            <div className="bg-white border border-slate-200 rounded overflow-hidden">
                <table className="w-full crm-table" data-testid="users-table">
                    <thead>
                        <tr>
                            <th>{t("name")}</th>
                            <th>Email / NIPT</th>
                            <th>{t("role")}</th>
                            <th>{t("user_type")}</th>
                            <th>{t("status")}</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={6} className="text-center py-10 text-slate-400">{t("loading")}</td></tr>
                        ) : users.length === 0 ? (
                            <tr><td colSpan={6} className="text-center py-10 text-slate-400">{t("no_data")}</td></tr>
                        ) : users.map(u => (
                            <tr key={u.id} data-testid={`user-row-${u.id}`}>
                                <td className="font-medium text-sm text-slate-900">{u.name}</td>
                                <td className="text-sm">
                                    {u.email && <span>{u.email}</span>}
                                    {u.nipt && <span className="font-mono">{u.nipt}</span>}
                                </td>
                                <td>
                                    <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${ROLE_CLASS[u.role] || "badge-inactive"}`}>
                                        {ROLE_LABELS[u.role] || u.role}
                                    </span>
                                </td>
                                <td className="text-xs text-slate-500 capitalize">
                                    {u.user_type === "client" ? t("portal_client") : t("system_user")}
                                </td>
                                <td>
                                    <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${u.is_active !== false ? "badge-active" : "badge-inactive"}`}>
                                        {u.is_active !== false ? t("active") : t("inactive")}
                                    </span>
                                </td>
                                <td>
                                    <div className="flex items-center gap-1">
                                        <button onClick={() => handleToggleActive(u)} title={u.is_active !== false ? "Çaktivizo" : "Aktivizo"}
                                            className={`p-1.5 rounded transition-colors ${u.is_active !== false ? "hover:bg-amber-50 text-slate-400 hover:text-amber-600" : "hover:bg-emerald-50 text-slate-400 hover:text-emerald-600"}`}
                                            data-testid={`toggle-user-${u.id}`}>
                                            {u.is_active !== false ? <UserX size={14} /> : <UserCheck size={14} />}
                                        </button>
                                        <button onClick={() => openEdit(u)}
                                            className="p-1.5 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-700" data-testid={`edit-user-${u.id}`}>
                                            <Edit size={14} />
                                        </button>
                                        <button onClick={() => handleDelete(u)}
                                            className="p-1.5 rounded hover:bg-rose-50 text-slate-400 hover:text-rose-600" data-testid={`delete-user-${u.id}`}>
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <Dialog open={showModal} onOpenChange={setShowModal}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>{editItem ? "Modifiko Përdoruesin" : "Shto Përdorues të Ri"}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-3 mt-2">
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 block mb-1">Lloji</label>
                                <Select value={form.user_type} onValueChange={v => f("user_type", v)} disabled={!!editItem}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="staff">{t("system_user")}</SelectItem>
                                        <SelectItem value="client">{t("portal_client")}</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 block mb-1">Roli</label>
                                <Select value={form.role} onValueChange={v => f("role", v)}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="admin">Admin</SelectItem>
                                        <SelectItem value="staff">Staf</SelectItem>
                                        <SelectItem value="support">Support</SelectItem>
                                        {form.user_type === "client" && <SelectItem value="client">Klient</SelectItem>}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div>
                            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 block mb-1">Emri *</label>
                            <input required value={form.name} onChange={e => f("name", e.target.value)}
                                data-testid="user-name-input"
                                className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#E7212E]/20 focus:border-[#E7212E]" />
                        </div>
                        {form.user_type === "staff" ? (
                            <div>
                                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 block mb-1">Email *</label>
                                <input type="email" value={form.email} onChange={e => f("email", e.target.value)}
                                    required={!editItem}
                                    data-testid="user-email-input"
                                    className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#E7212E]/20 focus:border-[#E7212E]" />
                            </div>
                        ) : (
                            <div>
                                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 block mb-1">NIPT (Username) *</label>
                                <input value={form.nipt} onChange={e => f("nipt", e.target.value.toUpperCase())}
                                    required={!editItem}
                                    data-testid="user-nipt-input"
                                    className="w-full px-3 py-2 border border-slate-300 rounded text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#E7212E]/20 focus:border-[#E7212E]" />
                            </div>
                        )}
                        <div>
                            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 block mb-1">
                                {editItem ? t("new_password") : "Fjalëkalimi *"}
                            </label>
                            <input type="password" value={form.password} onChange={e => f("password", e.target.value)}
                                required={!editItem}
                                data-testid="user-password-input"
                                placeholder={editItem ? "Lër bosh për të mos ndryshuar" : ""}
                                className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#E7212E]/20 focus:border-[#E7212E]" />
                        </div>
                        {error && <p className="text-rose-600 text-sm bg-rose-50 p-2 rounded">{error}</p>}
                        <div className="flex justify-end gap-2 pt-2">
                            <button type="button" onClick={() => setShowModal(false)}
                                className="px-4 py-2 border border-slate-200 rounded text-sm text-slate-700 hover:bg-slate-50">{t("cancel")}</button>
                            <button type="submit" disabled={saving} data-testid="save-user-btn"
                                className="px-4 py-2 bg-[#E7212E] hover:bg-[#C91C26] text-white text-sm font-medium rounded disabled:opacity-60">
                                {saving ? "Duke ruajtur..." : t("save")}
                            </button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
